import React, { lazy, Suspense, useEffect } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Routes, Route, useLocation, useParams } from "react-router-dom";
import "./index.css";
import { ClaweaseSite } from "./clawease/ClaweaseSite";
import { CookieConsentBanner } from "./clawease/components/CookieConsentBanner";
import { LeadFormPage } from "./clawease/components/LeadFormPage";
import { DownloadPage } from "./clawease/components/DownloadPage";
import { NotFoundPage } from "./clawease/components/NotFoundPage";
import { PrivacyPolicyPage } from "./clawease/components/PrivacyPolicyPage";
import { LegalDocumentPage } from "./clawease/components/LegalDocumentPage";
import { AboutPage } from "./clawease/components/AboutPage";
import { RouteLoadingPage } from "./clawease/components/RouteLoadingPage";
import { IndustrySolutionPage } from "./clawease/components/IndustrySolutionPage";
import { Seo } from "./clawease/components/Seo";
import { createDentalScrollRestorationGuard } from "./clawease/dentalReloadScroll";
import { getDentalSectionAnchorTop } from "./clawease/dentalSectionNavigation";
import { hashAnchorId } from "./clawease/urlParams";

const preserveDentalScroll = createDentalScrollRestorationGuard(window);

const CallflowSite = lazy(() => import("./callflow/CallflowSite"));
const DentalBookingDemoPage = lazy(() => import("./clawease/components/demo/DentalBookingDemoPage").then(m => ({ default: m.DentalBookingDemoPage })));
const WsInspectorPage = lazy(() => import("./clawease/components/demo/WsInspectorPage").then(m => ({ default: m.WsInspectorPage })));
const routerBasename = import.meta.env.BASE_URL === "/" ? undefined : import.meta.env.BASE_URL.replace(/\/$/, "");

// 在开发环境和客户端导航中将旧官网地址收敛到产品正式路径。
function LegacyRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

// 将旧语言前缀下的任意子页面映射到同一产品命名空间。
function LegacyPrefixRedirect({ to }: { to: string }) {
  const rawRemainder = useParams()["*"] || "";
  const normalized = rawRemainder.replace(/\/index\.html$/, "").replace(/\/+$/, "");
  const remainder = normalized === "index.html" ? ""
    : normalized === "terms" ? "terms-of-use"
    : to.startsWith("/cn/") && normalized === "privacy-policy" ? "privacy"
    : normalized;
  return <LegacyRedirect to={`${to}${remainder ? `/${remainder}` : ""}`} />;
}

function HashScroller() {
  const location = useLocation();

  useEffect(() => {
    // Native restoration knows the actual reading position, even when an older
    // anchor remains in the URL after the user has scrolled to another section.
    if (preserveDentalScroll(location)) return;
    const anchorId = hashAnchorId(location.hash);
    if (!anchorId) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const timeouts: number[] = [];
    const scrollDelays = [0, 120, 320, 700, 1100];
    let centeredAnchor = false;

    const scrollToTarget = (behavior: ScrollBehavior) => {
      const target = document.getElementById(anchorId);

      if (target) {
        centeredAnchor = target.hasAttribute("data-dental-centered-anchor");
        if (centeredAnchor) {
          window.scrollTo({ top: getDentalSectionAnchorTop(target, window.scrollY, window.innerHeight), behavior });
        } else {
          target.scrollIntoView({ behavior, block: "start" });
        }
      }
    };

    scrollDelays.forEach((delay, index) => {
      timeouts.push(window.setTimeout(() => scrollToTarget(index === 0 ? "smooth" : "auto"), delay));
    });

    const cancelOnInput = () => {
      if (centeredAnchor) timeouts.forEach((timeout) => window.clearTimeout(timeout));
    };
    const inputs = ["wheel", "touchstart", "pointerdown", "keydown"] as const;
    inputs.forEach((name) => window.addEventListener(name, cancelOnInput, { passive: true }));

    return () => {
      timeouts.forEach((timeout) => window.clearTimeout(timeout));
      inputs.forEach((name) => window.removeEventListener(name, cancelOnInput));
    };
  }, [location]);

  return null;
}

const rootElement = document.getElementById("root")!;
const app = (
  <React.StrictMode>
    <BrowserRouter basename={routerBasename}>
      <HashScroller />
      <Routes>
        {/* AI Receptionist 产品官网：原英文官网为美国站，香港站使用独立路径。 */}
        <Route path="/en/ai-receptionist" element={<ClaweaseSite defaultLang="en" />} />
        <Route path="/ai-receptionist" element={<ClaweaseSite defaultLang="en" />} />
        <Route path="/cn/ai-receptionist" element={<LegacyRedirect to="/hk/ai-receptionist" />} />
        <Route path="/hk/ai-receptionist" element={<ClaweaseSite defaultLang="zh-HK" />} />
        <Route path="/hk/ai-receptionist/contact" element={<LeadFormPage defaultLang="zh" variant="hk" />} />
        <Route path="/ai-receptionist/contact" element={<LeadFormPage defaultLang="en" />} />
        <Route path="/cn/ai-receptionist/contact" element={<LeadFormPage defaultLang="zh" />} />
        <Route path="/ai-receptionist/download" element={<DownloadPage defaultLang="en" />} />
        <Route path="/cn/ai-receptionist/download" element={<DownloadPage defaultLang="zh" />} />
        <Route path="/ai-receptionist/privacy-policy" element={<PrivacyPolicyPage defaultLang="en" />} />
        <Route path="/cn/ai-receptionist/privacy" element={<PrivacyPolicyPage defaultLang="zh" />} />
        <Route path="/ai-receptionist/cookie-policy" element={<LegalDocumentPage defaultLang="en" document="cookie" />} />
        <Route path="/cn/ai-receptionist/cookie-policy" element={<LegalDocumentPage defaultLang="zh" document="cookie" />} />
        <Route path="/ai-receptionist/terms-of-use" element={<LegalDocumentPage defaultLang="en" document="terms" />} />
        <Route path="/cn/ai-receptionist/terms-of-use" element={<LegalDocumentPage defaultLang="zh" document="terms" />} />
        <Route path="/ai-receptionist/about" element={<AboutPage defaultLang="en" />} />
        <Route path="/cn/ai-receptionist/about" element={<AboutPage defaultLang="zh" />} />
        <Route path="/ai-receptionist/solutions/:industrySlug" element={<IndustrySolutionPage />} />
        <Route
          path="/cn/ai-receptionist/demo/dental-booking"
          element={
            <>
              <Seo lang="zh" page="demo" />
              <Suspense fallback={<RouteLoadingPage lang="zh" kind="demo" />}>
                <DentalBookingDemoPage defaultLang="zh" />
              </Suspense>
            </>
          }
        />
        <Route
          path="/ai-receptionist/demo/dental-booking"
          element={
            <>
              <Seo lang="en" page="demo" />
              <Suspense fallback={<RouteLoadingPage lang="en" kind="demo" />}>
                <DentalBookingDemoPage defaultLang="en" />
              </Suspense>
            </>
          }
        />
        <Route
          path="/cn/ai-receptionist/demo/ws-inspector"
          element={
            <>
              <Seo lang="zh" page="ws-inspector" />
              <Suspense fallback={<RouteLoadingPage lang="zh" kind="diagnostics" />}>
                <WsInspectorPage defaultLang="zh" />
              </Suspense>
            </>
          }
        />
        <Route
          path="/ai-receptionist/demo/ws-inspector"
          element={
            <>
              <Seo lang="en" page="ws-inspector" />
              <Suspense fallback={<RouteLoadingPage lang="en" kind="diagnostics" />}>
                <WsInspectorPage defaultLang="en" />
              </Suspense>
            </>
          }
        />
        <Route
          path="/cn/ai-receptionist/*"
          element={<NotFoundPage defaultLang="zh" />}
        />
        <Route
          path="/ai-receptionist/*"
          element={<NotFoundPage defaultLang="en" />}
        />

        {/* 旧官网地址的客户端兼容跳转；生产环境由 Nginx 返回 301。 */}
        <Route path="/" element={<ClaweaseSite defaultLang="en" />} />
        <Route path="/en" element={<LegacyRedirect to="/ai-receptionist" />} />
        <Route path="/cn" element={<LegacyRedirect to="/hk/ai-receptionist" />} />
        <Route path="/contact" element={<LegacyRedirect to="/ai-receptionist/contact" />} />
        <Route path="/en/contact" element={<LegacyRedirect to="/ai-receptionist/contact" />} />
        <Route path="/cn/contact" element={<LegacyRedirect to="/cn/ai-receptionist/contact" />} />
        <Route path="/download" element={<LegacyRedirect to="/ai-receptionist/download" />} />
        <Route path="/en/download" element={<LegacyRedirect to="/ai-receptionist/download" />} />
        <Route path="/cn/download" element={<LegacyRedirect to="/cn/ai-receptionist/download" />} />
        <Route path="/privacy-policy" element={<LegacyRedirect to="/ai-receptionist/privacy-policy" />} />
        <Route path="/en/privacy-policy" element={<LegacyRedirect to="/ai-receptionist/privacy-policy" />} />
        <Route path="/cn/privacy" element={<LegacyRedirect to="/cn/ai-receptionist/privacy" />} />
        <Route path="/cn/privacy-policy" element={<LegacyRedirect to="/cn/ai-receptionist/privacy" />} />
        <Route path="/cookie-policy" element={<LegacyRedirect to="/ai-receptionist/cookie-policy" />} />
        <Route path="/en/cookie-policy" element={<LegacyRedirect to="/ai-receptionist/cookie-policy" />} />
        <Route path="/cn/cookie-policy" element={<LegacyRedirect to="/cn/ai-receptionist/cookie-policy" />} />
        <Route path="/terms-of-use" element={<LegacyRedirect to="/ai-receptionist/terms-of-use" />} />
        <Route path="/en/terms-of-use" element={<LegacyRedirect to="/ai-receptionist/terms-of-use" />} />
        <Route path="/en/terms" element={<LegacyRedirect to="/ai-receptionist/terms-of-use" />} />
        <Route path="/cn/terms-of-use" element={<LegacyRedirect to="/cn/ai-receptionist/terms-of-use" />} />
        <Route path="/cn/terms" element={<LegacyRedirect to="/cn/ai-receptionist/terms-of-use" />} />
        <Route path="/about" element={<LegacyRedirect to="/ai-receptionist/about" />} />
        <Route path="/en/about" element={<LegacyRedirect to="/ai-receptionist/about" />} />
        <Route path="/cn/about" element={<LegacyRedirect to="/cn/ai-receptionist/about" />} />
        <Route path="/en/demo/dental-booking" element={<LegacyRedirect to="/ai-receptionist/demo/dental-booking" />} />
        <Route path="/cn/demo/dental-booking" element={<LegacyRedirect to="/cn/ai-receptionist/demo/dental-booking" />} />
        <Route path="/en/demo/ws-inspector" element={<LegacyRedirect to="/ai-receptionist/demo/ws-inspector" />} />
        <Route path="/cn/demo/ws-inspector" element={<LegacyRedirect to="/cn/ai-receptionist/demo/ws-inspector" />} />
        <Route path="/en/*" element={<LegacyPrefixRedirect to="/ai-receptionist" />} />
        <Route path="/cn/*" element={<LegacyPrefixRedirect to="/cn/ai-receptionist" />} />
        <Route path="/clawease/en/*" element={<LegacyPrefixRedirect to="/ai-receptionist" />} />
        <Route path="/clawease/cn/*" element={<LegacyPrefixRedirect to="/cn/ai-receptionist" />} />
        <Route path="/callflow" element={<LegacyRedirect to="/callflow/cn" />} />
        <Route path="/clawease" element={<LegacyRedirect to="/ai-receptionist" />} />
        <Route path="/clawease/index.html" element={<LegacyRedirect to="/ai-receptionist" />} />

        {/* CallFlow 旧官网 */}
        <Route
          path="/callflow/cn"
          element={
            <Suspense fallback={null}>
              <CallflowSite defaultLang="zh" />
            </Suspense>
          }
        />
        <Route
          path="/callflow/en"
          element={
            <Suspense fallback={null}>
              <CallflowSite defaultLang="en" />
            </Suspense>
          }
        />

        <Route
          path="*"
          element={<NotFoundPage defaultLang="en" />}
        />
      </Routes>
      <CookieConsentBanner />
    </BrowserRouter>
  </React.StrictMode>
);

if (rootElement.dataset.prerendered === "true") {
  hydrateRoot(rootElement, app, {
    onRecoverableError(error) {
      const hydrationWindow = window as Window & { __claweaseHydrationErrors?: string[] };
      hydrationWindow.__claweaseHydrationErrors ??= [];
      hydrationWindow.__claweaseHydrationErrors.push(error instanceof Error ? error.message : String(error));
      console.error("ClawEase hydration recovery:", error);
    },
  });
} else {
  createRoot(rootElement).render(app);
}
